import React, { useEffect, useState } from "react";
import { Alert, Button, Modal, ModalBody, ModalHeader, Spinner, Table } from "reactstrap";
import { ACCURACY_TAGS, NoteTypeCell } from "./NoteAccuracyPanel";
import NoteReviewReader from "./NoteReviewReader";

const dateLabel = (value) => value
  ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
  : "—";

// The signed notes behind one common-edit theme. A note opens in the Note Editing
// reader; like the feedback dashboard, the reader replaces this list (no nested
// modals) and returns to it on close.
export default function CommonEditThemeNotes({ apiClient, theme, onClose }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [reviewIndex, setReviewIndex] = useState(null);

  useEffect(() => {
    let active = true;
    setResult(null);
    setError("");
    apiClient.JMPost("/v1/notes/common-edits/notes", { noteIds: theme.noteIds }).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setResult(response);
    }).catch(() => {
      if (active) setError("Unable to load these notes. Try again.");
    });
    return () => { active = false; };
  }, [apiClient, theme, retry]);

  const notes = result?.notes || [];
  if (reviewIndex !== null && notes[reviewIndex]) {
    return <NoteReviewReader
      apiClient={apiClient} note={notes[reviewIndex]} index={reviewIndex} count={notes.length}
      preferredSection={theme.sections?.[0]} tagLabels={ACCURACY_TAGS}
      onClose={() => setReviewIndex(null)} onNavigate={setReviewIndex}
    />;
  }

  const unopenable = result ? result.requested - notes.length : 0;
  return (
    <Modal isOpen toggle={onClose} centered className="note-intel-theme-modal" returnFocusAfterClose>
      <ModalHeader toggle={onClose}>
        <span>{theme.name}</span>
        {result && <small className="d-block text-muted">
          {theme.noteIds.length < theme.noteCount
            ? `${theme.noteIds.length} of ${theme.noteCount.toLocaleString()} notes in this theme`
            : `${theme.noteCount.toLocaleString()} notes in this theme`}
        </small>}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted">{theme.definition} Open a note to see what the clinician changed.</p>
        {error && <Alert color="danger">{error} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>}
        {!result && !error && <div><Spinner size="sm" /> Loading notes…</div>}
        {result && <>
          {unopenable > 0 && (
            <p className="text-muted">
              {unopenable} {unopenable === 1 ? "note is" : "notes are"} not shown because {unopenable === 1 ? "it has" : "they have"} not been signed off or compared.
            </p>
          )}
          {!notes.length ? <p className="text-muted">None of this theme's notes can be opened.</p> : (
            <div className="table-responsive">
              <Table className="note-accuracy-table" hover>
                <thead><tr><th>Signed / note type</th><th>Clinician / EMR</th><th>Text change</th><th><span className="sr-only">Review</span></th></tr></thead>
                <tbody>{notes.map((note, index) => <tr key={note.noteId}>
                  <td>{dateLabel(note.signedAt)}<small><NoteTypeCell note={note} /></small></td>
                  <td>{note.userId}<small>{note.emr || "Unknown"}</small></td>
                  <td>{note.pctChanged == null ? "—" : `${Number(note.pctChanged).toFixed(1)}%`}</td>
                  <td><Button size="sm" outline onClick={() => setReviewIndex(index)} aria-label={`Review ${note.noteType || "note"} for ${note.userId}`}>Review</Button></td>
                </tr>)}</tbody>
              </Table>
            </div>
          )}
        </>}
      </ModalBody>
    </Modal>
  );
}
